// @ts-check

/**
 * @file Renders uploaded file search and list management.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useRouter} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import buildRoutePath from "../../src/shared/build-route-path"
import Button from "../../src/components/button"
import Card from "../../src/components/card"
import errorMessage from "../../src/shared/error-message"
import Link from "../../src/components/link"
import Modal from "../../src/components/modal"
import {pushQueryParamUpdates} from "on-location-changed/build/query-param-updates"
import useCurrentQueryParams from "on-location-changed/build/use-current-query-params"
import useSelectedQueryParams from "on-location-changed/build/use-selected-query-params"
import {optionalRouteParam} from "../../src/shared/route-params"
import ScreenTitle from "../../src/components/screen-title"
import Text from "@kaspernj/api-maker/build/utils/text"
import TextInput from "../../src/components/text-input"
import {UploadedFile} from "../../src/frontend-models/uploaded-file"
import useThemeColors, {useThemeName} from "../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {Record<string, string | string[] | undefined>} RouteQueryParams */
/** @type {Record<string, object>} */
const styles = {}
const UPLOADED_FILES_QUERY_PARAM_SELECTOR = ["title_cont"]

/** @typedef {{[key: string]: unknown}} UploadedFilesIndexScreenProps */

/**
 * @typedef {object} UploadedFilesIndexScreenState
 * @property {string | null} confirmingDeleteId Uploaded file id awaiting delete confirmation.
 * @property {boolean} loading Whether uploaded files are loading.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 * @property {UploadedFile[]} uploadedFiles Loaded uploaded files.
 */

/**
 * Uploaded files index screen using frontend models.
 * @augments {ShapeComponent<UploadedFilesIndexScreenProps, UploadedFilesIndexScreenState>}
 */
class UploadedFilesIndexScreen extends ShapeComponent {
  /** @type {() => RouteQueryParams} */
  currentQueryParams
  /** @type {RouteQueryParams} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {string} */
  searchTitle = ""
  /** @type {UploadedFilesIndexScreenState} */
  state = {
    confirmingDeleteId: /** @type {string | null} */ null,
    loading: true,
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null,
    uploadedFiles: /** @type {UploadedFile[]} */ []
  }

  /** Initializes search params and uploaded file loading. */
  setup() {
    useLocale()
    const params = /** @type {RouteQueryParams} */ (useSelectedQueryParams(UPLOADED_FILES_QUERY_PARAM_SELECTOR))
    const currentQueryParams = /** @type {() => RouteQueryParams} */ (useCurrentQueryParams())
    const router = useRouter()

    this.searchTitle = optionalRouteParam(params, "title_cont", "Title filter") ?? ""
    this.currentQueryParams = currentQueryParams
    this.params = params
    this.router = router
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          const allFiles = await UploadedFile
            .select({
              UploadedFile: ["id", "title"]
            })
            .toArray()
          if (cancelled) return
          this.s.loading = false
          this.s.uploadedFiles = allFiles
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
          throw error
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [])
  }

  /** Updates the current title filter route parameter. */
  onSearchPress = () => {
    const title = this.searchTitle.trim()
    pushQueryParamUpdates(this.tt.router, this.tt.currentQueryParams(), "/uploaded-files", {
      title_cont: title || undefined
    })
  }

  /** Closes the uploaded-file delete confirmation. */
  onCancelDeletePress = () => {
    this.s.confirmingDeleteId = null
  }

  /**
   * Renders uploaded file search and results.
   * @returns {ReactNode} Uploaded files list content.
   */
  render() {
    const {confirmingDeleteId, loading, status, uploadedFiles} = this.s
    const searchTitle = this.searchTitle
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const filteredFiles = searchTitle
      ? uploadedFiles.filter((/** @type {UploadedFile} */ file) => {
          const title = file.title()
          return (
            title && title.toLowerCase().includes(searchTitle.toLowerCase())
          )
        })
      : uploadedFiles
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="uploadedFilesIndexScreen"
      >
        <ScreenTitle>{_("Uploaded files")}</ScreenTitle>
        <View
          testID="uploadedFilesIndexScreen/content"
        >
          <Text
            style={styles.title ||= {
              fontSize: 24,
              fontWeight: 700,
              marginBottom: 16
            }}
            testID="uploadedFilesIndexScreen/title"
          >
            {_("Uploaded files")}
          </Text>
          <Card
            style={styles.filterCard ||= {
              gap: 12,
              marginBottom: 16
            }}
            testID="uploadedFilesIndexScreen/filterCard"
          >
            <TextInput
              defaultValue={searchTitle}
              onChangeText={(value) => {
                this.searchTitle = value
              }}
              placeholder={_("Title")}
              testID="uploadedFilesIndexScreen/titleFilter"
            />
            <View
              style={styles.searchActions ||= {alignSelf: "flex-start"}}
            >
              <Button
                iconName="magnifying-glass"
                label={_("Search")}
                onPress={this.tt.onSearchPress}
                testID="uploadedFilesIndexScreen/searchButton"
                variant="neutral"
              />
            </View>
          </Card>
          {status &&
            <Text
              style={styles[`status-${themeName}-${status.kind}`] ||= {
                color: status.kind === "error" ? colors.dangerText : colors.successText,
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="uploadedFilesIndexScreen/status"
            >
              {status.text}
            </Text>
          }
          <Modal
            cardStyle={styles[`confirmModalCard-${themeName}`] ||= {
              borderColor: colors.danger,
              gap: 12,
              maxWidth: 420
            }}
            isVisible={Boolean(confirmingDeleteId)}
            onRequestClose={this.tt.onCancelDeletePress}
            testID="uploadedFilesIndexScreen/deleteConfirm"
          >
            <Text
              style={styles[`confirmTitle-${themeName}`] ||= {
                color: colors.dangerText,
                fontSize: 16,
                fontWeight: 700
              }}
              testID="uploadedFilesIndexScreen/deleteConfirm/title"
            >
              {_("Delete file")}
            </Text>
            <View
              style={styles.confirmActions ||= {
                flexDirection: "row",
                gap: 8
              }}
              testID="uploadedFilesIndexScreen/deleteConfirm/actions"
            >
              <Button
                iconName="trash"
                label={_("Delete")}
                onPress={this.tt.onConfirmDeletePress}
                testID="uploadedFilesIndexScreen/deleteConfirm/confirmButton"
                variant="destructive"
              />
              <Button
                iconName="xmark"
                label={_("Cancel")}
                onPress={this.tt.onCancelDeletePress}
                testID="uploadedFilesIndexScreen/deleteConfirm/cancelButton"
                variant="outline"
              />
            </View>
          </Modal>
          {loading &&
            <Text
              style={styles[`loadingText-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 16
              }}
              testID="uploadedFilesIndexScreen/loadingText"
            >
              {_("Loading...")}
            </Text>
          }
          {!loading && filteredFiles.length === 0 &&
            <Text
              style={styles[`emptyText-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 16
              }}
              testID="uploadedFilesIndexScreen/emptyText"
            >
              {_("No files found")}
            </Text>
          }
          {!loading &&
            filteredFiles.map((/** @type {UploadedFile} */ file) => {
              const fileId = String(file.id())
              return (
                <Card
                  key={fileId}
                  style={styles.fileRow ||= {marginBottom: 12}}
                  testID={`uploadedFilesIndexScreen/row-${fileId}`}
                >
                  <View
                    style={styles.fileRowHeader ||= {
                      alignItems: "center",
                      flexDirection: "row",
                      gap: 8,
                      justifyContent: "space-between"
                    }}
                    testID={`uploadedFilesIndexScreen/row-${fileId}/header`}
                  >
                    <Link
                      href={buildRoutePath(this.tt.params, `/uploaded-files/${fileId}`)}
                      style={styles.fileNameLink ||= {flex: 1}}
                      testID={`uploadedFilesIndexScreen/row-${fileId}/showButton`}
                    >
                      <Text
                        style={styles.fileName ||= {
                          fontSize: 16,
                          fontWeight: 600
                        }}
                        testID={`uploadedFilesIndexScreen/row-${fileId}/title`}
                      >
                        {file.title() || fileId}
                      </Text>
                    </Link>
                    <View
                      style={styles.fileRowActions ||= {
                        flexDirection: "row",
                        gap: 8
                      }}
                      testID={`uploadedFilesIndexScreen/row-${fileId}/actions`}
                    >
                      <Button
                        href={buildRoutePath(this.tt.params, `/uploaded-files/${fileId}/edit`)}
                        iconName="pen-to-square"
                        label={_("Edit")}
                        testID={`uploadedFilesIndexScreen/row-${fileId}/editButton`}
                      />
                      <Button
                        iconName="trash"
                        label={_("Delete")}
                        onPress={() => {
                          this.s.confirmingDeleteId = fileId
                          this.s.status = null
                        }}
                        testID={`uploadedFilesIndexScreen/row-${fileId}/deleteButton`}
                        variant="destructive"
                      />
                    </View>
                  </View>
                </Card>
              )
            })}
        </View>
      </View>
    )
  }

  /**
   * Deletes the confirmed uploaded file.
   * @returns {Promise<void>} Resolves when delete handling completes.
   * @rejects {unknown} When deletion fails.
   */
  onConfirmDeletePress = async () => {
    const {confirmingDeleteId, uploadedFiles} = this.s
    if (!confirmingDeleteId) return
    try {
      const file = await UploadedFile
        .select({
          UploadedFile: ["id"]
        })
        .find(confirmingDeleteId)
      await file.destroy()
      this.s.confirmingDeleteId = null
      this.s.status = {
        kind: "success",
        text: _("The file was deleted")
      }
      this.s.uploadedFiles = uploadedFiles.filter(
        (/** @type {UploadedFile} */ f) =>
          String(f.id()) !== confirmingDeleteId
      )
    } catch (error) {
      this.s.confirmingDeleteId = null
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }
}

/** @type {NamedExoticComponent<UploadedFilesIndexScreenProps>} */
const UploadedFilesIndexScreenComponent = memo(shapeComponent(UploadedFilesIndexScreen))

export default UploadedFilesIndexScreenComponent
